/**
 * SidebarHeader.tsx — 8210-A
 *
 * Header for the CommentSidebar panel.
 * Shows comment count, up/down navigation arrows, and a three-dot menu
 * with "Delete All" action.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import DotsActionMenu from "../../DotsActionMenu";
import {
  SidebarHeaderRow,
  NavButton,
  CommentFilterTriggerButton,
  CommentFilterSelectWrapper,
  CommentFilterCountPill,
  CommentFilterDropdownMenu,
  CommentFilterOptionButton,
  CommentFilterOptionLabel,
} from "./styles";
import { sendAppEvent } from "src/Utils";
import {
  COMMENT_FILTER_TYPES,
  getCommentFilterLabel,
} from "./commentStatus";

interface SidebarHeaderProps {
  selectedFilter: string;
  commentCounts: Record<string, number>;
  onNavigateUp: () => void;
  onNavigateDown: () => void;
  onDeleteAll: (filterType: string) => void;
  onFilterChange: (filterType: string) => void;
  canNavigateUp: boolean;
  canNavigateDown: boolean;
}

export function SidebarHeader({
  selectedFilter,
  commentCounts,
  onNavigateUp,
  onNavigateDown,
  onDeleteAll,
  onFilterChange,
  canNavigateUp,
  canNavigateDown,
}: SidebarHeaderProps): JSX.Element {
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const selectedCount = commentCounts[selectedFilter] ?? 0;
  const selectedFilterLabel = getCommentFilterLabel(selectedFilter);
  const filterOptions = useMemo(
    () => [
      COMMENT_FILTER_TYPES.OPEN,
      COMMENT_FILTER_TYPES.RESOLVED,
      COMMENT_FILTER_TYPES.IGNORED,
    ],
    [],
  );

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!filterContainerRef.current?.contains(target)) {
        setIsFilterMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const menuActions = {
    deleteAll: {
      label: "Delete All",
      onClick: () => onDeleteAll(selectedFilter),
      danger: true,
    },
  };

  return (
    <SidebarHeaderRow data-testid="sidebar-header">
      <div className="flex items-center gap-1">
        <SVG
          src="/assets/comment-icon.svg"
          aria-hidden="true"
          width={24}
          height={24}
        />
        <CommentFilterSelectWrapper ref={filterContainerRef}>
          <CommentFilterTriggerButton
            type="button"
            className="use-component-styles"
            data-testid="comment-filter-trigger"
            aria-label="Filter comments by status"
            aria-haspopup="listbox"
            aria-expanded={isFilterMenuOpen}
            onClick={() => setIsFilterMenuOpen((prev) => !prev)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setIsFilterMenuOpen(false);
                return;
              }
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setIsFilterMenuOpen((prev) => !prev);
              }
            }}
          >
            <CommentFilterOptionLabel className="trigger-label">{selectedFilterLabel}</CommentFilterOptionLabel>
            <CommentFilterCountPill
              data-testid="comment-filter-count-pill"
              aria-hidden="true"
            >
              {selectedCount}
            </CommentFilterCountPill>
            <SVG
              src="/assets/chevron-down.svg"
              aria-hidden="true"
            />
          </CommentFilterTriggerButton>

          {isFilterMenuOpen && (
            <CommentFilterDropdownMenu
              role="listbox"
              aria-label="Filter comments by status options"
            >
              {filterOptions.map((filterType) => (
                <CommentFilterOptionButton
                  key={filterType}
                  type="button"
                  className="use-component-styles"
                  role="option"
                  data-testid={`comment-filter-option-${filterType}`}
                  aria-selected={selectedFilter === filterType}
                  $active={selectedFilter === filterType}
                  onClick={() => {
                    if (filterType !== selectedFilter) {
                      sendAppEvent("Comment Filter - Changed", {
                        from_filter: selectedFilter,
                        to_filter: filterType,
                      });
                    }
                    onFilterChange(filterType);
                    setIsFilterMenuOpen(false);
                  }}
                >
                  <CommentFilterOptionLabel>{getCommentFilterLabel(filterType)}</CommentFilterOptionLabel>
                  <CommentFilterCountPill aria-hidden="true">{commentCounts[filterType] ?? 0}</CommentFilterCountPill>
                </CommentFilterOptionButton>
              ))}
            </CommentFilterDropdownMenu>
          )}
        </CommentFilterSelectWrapper>
      </div>

      <div className="flex items-center gap-1">
        <div className="flex items-center gap-0">
          <NavButton
            role="button"
            tabIndex={canNavigateUp ? 0 : -1}
            aria-label="Previous comment"
            aria-disabled={!canNavigateUp}
            $disabled={!canNavigateUp}
            onClick={canNavigateUp ? onNavigateUp : undefined}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (canNavigateUp && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onNavigateUp();
              }
            }}
          >
            <SVG
              src="/assets/chevron-down.svg"
              aria-hidden="true"
              style={{ transform: "rotate(180deg)" }}
            />
          </NavButton>

          <NavButton
            role="button"
            tabIndex={canNavigateDown ? 0 : -1}
            aria-label="Next comment"
            aria-disabled={!canNavigateDown}
            $disabled={!canNavigateDown}
            onClick={canNavigateDown ? onNavigateDown : undefined}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (canNavigateDown && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onNavigateDown();
              }
            }}
          >
            <SVG
              src="/assets/chevron-down.svg"
              aria-hidden="true"
            />
          </NavButton>
        </div>

        <DotsActionMenu
          actions={menuActions}
          smallDots={true}
          position="bottom-end"
        />
      </div>
    </SidebarHeaderRow>
  );
}

export default SidebarHeader;
